import React, { useCallback, useEffect, useId, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { analyticsApi, chargersApi, sessionsApi, alarmsApi } from '@/lib/api';
import { useWebSocket } from '@/context/WebSocketContext';
import { Button } from '@/components/ui/button';
import { StatusBadge, OnlineIndicator, SeverityBadge } from '@/components/StatusBadge';
import { formatEnergy, formatCurrency, timeAgo } from '@/lib/utils';
import {
  AreaChart, Area, BarChart, Bar, LineChart, Line,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts';
import {
  Battery, BatteryCharging, Zap, DollarSign, AlertTriangle, Activity, RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';
import {
  Page, GlassCard, PageHeader, StatCard, SectionHeading, EmptyState, ErrorState, PageSkeleton,
  chartTooltipStyle, chartAxisProps, errorMessage
} from '@/components/glass/surfaces';

const shortDate = (value) => (typeof value === 'string' ? value.slice(5) : value);

const periodChange = (series, key) => {
  if (!Array.isArray(series) || series.length < 2) return null;
  const mid = Math.floor(series.length / 2);
  const sum = (items) => items.reduce((total, item) => total + (Number(item?.[key]) || 0), 0);
  const prior = sum(series.slice(0, mid));
  const recent = sum(series.slice(mid));
  if (prior <= 0) return null;
  return Math.round(((recent - prior) / prior) * 100);
};

const trendCaption = (change) => {
  if (change === null) return 'Last 14 days';
  if (change === 0) return 'Last 14 days · flat vs the prior week';
  const direction = change > 0 ? 'up' : 'down';
  return `Last 14 days · ${direction} ${Math.abs(change)}% vs the prior week`;
};

const DashboardPage = () => {
  const navigate = useNavigate();
  const { subscribe } = useWebSocket();
  const energyGradientId = useId().replace(/[^a-zA-Z0-9_-]/g, '');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [hasData, setHasData] = useState(false);
  const [reloadToken, setReloadToken] = useState(0);
  const [kpis, setKpis] = useState({
    total_chargers: 0,
    online_chargers: 0,
    active_sessions: 0,
    total_energy_kwh: 0,
    total_revenue: 0,
    active_alarms: 0
  });
  const [energyTrend, setEnergyTrend] = useState([]);
  const [sessionsTrend, setSessionsTrend] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [recentChargers, setRecentChargers] = useState([]);
  const [recentSessions, setRecentSessions] = useState([]);
  const [recentAlarms, setRecentAlarms] = useState([]);

  const fetchDashboardData = useCallback(async () => {
    try {
      const [kpisRes, energyRes, sessionsRes, revenueRes, chargersRes, sessionsListRes, alarmsRes] = await Promise.all([
        analyticsApi.getDashboard(),
        analyticsApi.getEnergyTrend({ days: 14 }),
        analyticsApi.getSessionsTrend({ days: 14 }),
        analyticsApi.getRevenueTrend({ days: 14 }),
        chargersApi.getAll(),
        sessionsApi.getAll({ limit: 5 }),
        alarmsApi.getAll({ limit: 5 })
      ]);

      setKpis(kpisRes.data);
      setEnergyTrend(energyRes.data || []);
      setSessionsTrend(sessionsRes.data || []);
      setRevenueTrend(revenueRes.data || []);
      setRecentChargers((chargersRes.data || []).slice(0, 5));
      setRecentSessions(sessionsListRes.data || []);
      setRecentAlarms(alarmsRes.data || []);
      setHasData(true);
      setError('');
    } catch (fetchError) {
      console.error('Failed to fetch dashboard data:', fetchError);
      setError(errorMessage(fetchError, 'Failed to load dashboard data'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchDashboardData();

    const unsubscribe = subscribe('dashboard', (message) => {
      if (message.type === 'alarm_created') {
        toast.error(`New alarm: ${message.alarm?.error_code || 'Alarm'}`, {
          description: message.alarm?.message
        });
      }
      if (['charger_online', 'charger_offline', 'transaction_start', 'transaction_stop', 'alarm_created'].includes(message.type)) {
        fetchDashboardData();
      }
    });

    return () => unsubscribe();
  }, [subscribe, fetchDashboardData, reloadToken]);

  if (loading && !hasData) {
    return <PageSkeleton testId="dashboard-loading" />;
  }

  if (!hasData && error) {
    return (
      <Page testId="dashboard-page">
        <PageHeader title="Dashboard" description="Live status, energy, and revenue across your network." />
        <ErrorState message={error} onRetry={() => { setLoading(true); setReloadToken((value) => value + 1); }} />
      </Page>
    );
  }

  return (
    <Page testId="dashboard-page">
      <PageHeader
        title="Dashboard"
        description="Live status, energy, and revenue across your network."
        actions={(
          <>
            <Button type="button" variant="outline" className="rounded-full" onClick={() => { setLoading(true); setReloadToken((value) => value + 1); }} aria-label="Refresh dashboard">
              <RefreshCw className="h-4 w-4" />
              Refresh
            </Button>
            <Button type="button" className="rounded-full" onClick={() => navigate('/chargers')} data-testid="view-chargers-btn">
              <Battery className="h-4 w-4" />
              View Chargers
            </Button>
          </>
        )}
      />

      {error ? <ErrorState title="Dashboard is out of date" message={error} onRetry={() => setReloadToken((value) => value + 1)} /> : null}

      <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
        <StatCard
          label="Total Chargers"
          value={kpis.total_chargers}
          hint={`${kpis.online_chargers} online`}
          icon={Battery}
          tone="accent"
          testId="kpi-total-chargers"
        />
        <StatCard
          label="Active Sessions"
          value={kpis.active_sessions}
          hint="Charging now"
          icon={BatteryCharging}
          tone="info"
          testId="kpi-active-sessions"
        />
        <StatCard
          label="Total Energy"
          value={formatEnergy(kpis.total_energy_kwh)}
          hint="All time"
          icon={Zap}
          tone="warning"
          testId="kpi-total-energy"
        />
        <StatCard
          label="Total Revenue"
          value={formatCurrency(kpis.total_revenue)}
          hint="All time"
          icon={DollarSign}
          tone="success"
          testId="kpi-total-revenue"
        />
      </div>

      {kpis.active_alarms > 0 && (
        <GlassCard className="flex flex-col gap-3 border-destructive/30 bg-destructive/10 p-4 sm:flex-row sm:items-center sm:justify-between" data-testid="active-alarms-alert">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-5 w-5 shrink-0 text-destructive" />
            <span className="font-medium">
              {kpis.active_alarms} active {kpis.active_alarms === 1 ? 'alarm needs' : 'alarms need'} attention
            </span>
          </div>
          <Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => navigate('/alarms')} data-testid="view-alarms-btn">
            View Alarms
          </Button>
        </GlassCard>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <GlassCard className="min-w-0 p-5 sm:p-6" data-testid="energy-trend-chart">
          <SectionHeading icon={Zap} title="Energy consumption" description={trendCaption(periodChange(energyTrend, 'energy_kwh'))} />
          {energyTrend.length === 0 ? (
            <div className="flex h-[240px] flex-col items-center justify-center text-center sm:h-[280px]">
              <Zap className="mb-2 h-6 w-6 text-muted-foreground" />
              <p className="text-sm font-medium">No energy data yet</p>
              <p className="mt-1 text-xs text-muted-foreground">Completed sessions will show up here.</p>
            </div>
          ) : (
            <div className="h-[240px] min-w-0 sm:h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={energyTrend}>
                  <defs>
                    <linearGradient id={energyGradientId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="hsl(var(--chart-1))" stopOpacity={0.35} />
                      <stop offset="95%" stopColor="hsl(var(--chart-1))" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="4 8" vertical={false} />
                  <XAxis dataKey="date" {...chartAxisProps} tickFormatter={shortDate} />
                  <YAxis {...chartAxisProps} width={40} />
                  <Tooltip
                    contentStyle={chartTooltipStyle}
                    formatter={(value) => [`${Number(value || 0).toFixed(2)} kWh`, 'Energy']}
                  />
                  <Area type="monotone" dataKey="energy_kwh" stroke="hsl(var(--chart-1))" fill={`url(#${energyGradientId})`} strokeWidth={2} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </GlassCard>

        <GlassCard className="min-w-0 p-5 sm:p-6" data-testid="revenue-trend-chart">
          <SectionHeading icon={DollarSign} title="Revenue" description={trendCaption(periodChange(revenueTrend, 'revenue'))} />
          {revenueTrend.length === 0 ? (
            <div className="flex h-[240px] flex-col items-center justify-center text-center sm:h-[280px]">
              <DollarSign className="mb-2 h-6 w-6 text-muted-foreground" />
              <p className="text-sm font-medium">No revenue data yet</p>
              <p className="mt-1 text-xs text-muted-foreground">Revenue appears after billed sessions.</p>
            </div>
          ) : (
            <div className="h-[240px] min-w-0 sm:h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={revenueTrend}>
                  <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="4 8" vertical={false} />
                  <XAxis dataKey="date" {...chartAxisProps} tickFormatter={shortDate} />
                  <YAxis {...chartAxisProps} width={40} />
                  <Tooltip
                    contentStyle={chartTooltipStyle}
                    cursor={{ fill: 'hsl(var(--foreground) / 0.06)' }}
                    formatter={(value) => [formatCurrency(value), 'Revenue']}
                  />
                  <Bar dataKey="revenue" fill="hsl(var(--chart-1))" radius={[8, 8, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </GlassCard>
      </div>

      <GlassCard className="min-w-0 p-5 sm:p-6" data-testid="sessions-trend-chart">
        <SectionHeading icon={Activity} title="Sessions" description={trendCaption(periodChange(sessionsTrend, 'count'))} />
        {sessionsTrend.length === 0 ? (
          <div className="flex h-[220px] flex-col items-center justify-center text-center">
            <Activity className="mb-2 h-6 w-6 text-muted-foreground" />
            <p className="text-sm font-medium">No sessions in this window</p>
          </div>
        ) : (
          <div className="h-[220px] min-w-0 sm:h-[250px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={sessionsTrend}>
                <CartesianGrid stroke="hsl(var(--border))" strokeDasharray="4 8" vertical={false} />
                <XAxis dataKey="date" {...chartAxisProps} tickFormatter={shortDate} />
                <YAxis {...chartAxisProps} width={32} allowDecimals={false} />
                <Tooltip
                  contentStyle={chartTooltipStyle}
                  formatter={(value) => [value, 'Sessions']}
                />
                <Line type="monotone" dataKey="count" stroke="hsl(var(--chart-2))" strokeWidth={2} dot={{ fill: 'hsl(var(--chart-2))', r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </GlassCard>

      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <GlassCard className="p-5 sm:p-6" data-testid="recent-chargers">
          <SectionHeading
            title="Chargers"
            description="Latest status"
            action={<Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => navigate('/chargers')}>View all</Button>}
          />
          {recentChargers.length === 0 ? (
            <EmptyState compact icon={Battery} title="No chargers registered" description="Charge points appear here after they connect." />
          ) : (
            <div className="space-y-2">
              {recentChargers.map((charger) => (
                <button
                  key={charger.charge_point_id}
                  type="button"
                  className="flex w-full items-center justify-between gap-3 rounded-2xl bg-black/[0.03] px-3 py-3 text-left transition-colors hover:bg-black/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:bg-white/[0.04] dark:hover:bg-white/[0.08]"
                  onClick={() => navigate(`/chargers/${charger.charge_point_id}`)}
                  data-testid={`charger-${charger.charge_point_id}`}
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">{charger.charge_point_id}</p>
                    <p className="truncate text-xs text-muted-foreground">{charger.vendor || 'Unknown vendor'} {charger.model || ''}</p>
                  </div>
                  <OnlineIndicator isOnline={charger.is_online} />
                </button>
              ))}
            </div>
          )}
        </GlassCard>

        <GlassCard className="p-5 sm:p-6" data-testid="recent-sessions">
          <SectionHeading
            title="Recent sessions"
            description="Latest charging activity"
            action={<Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => navigate('/sessions')}>View all</Button>}
          />
          {recentSessions.length === 0 ? (
            <EmptyState compact icon={BatteryCharging} title="No sessions yet" description="Sessions show up when a vehicle starts charging." />
          ) : (
            <div className="space-y-2">
              {recentSessions.map((session) => (
                <button
                  key={session.id}
                  type="button"
                  className="flex w-full items-center justify-between gap-3 rounded-2xl bg-black/[0.03] px-3 py-3 text-left transition-colors hover:bg-black/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:bg-white/[0.04] dark:hover:bg-white/[0.08]"
                  onClick={() => navigate(`/sessions/${session.id}`)}
                  data-testid={`session-${session.transaction_id}`}
                >
                  <div className="min-w-0">
                    <p className="font-medium">TX #{session.transaction_id}</p>
                    <p className="truncate text-xs text-muted-foreground">{session.charge_point_id}</p>
                  </div>
                  <div className="text-right">
                    <StatusBadge status={session.is_active ? 'Charging' : 'Finished'} variant="dot" />
                    {session.energy_kwh ? (
                      <p className="text-xs font-mono text-muted-foreground">{Number(session.energy_kwh).toFixed(2)} kWh</p>
                    ) : null}
                  </div>
                </button>
              ))}
            </div>
          )}
        </GlassCard>

        <GlassCard className="p-5 sm:p-6" data-testid="recent-alarms">
          <SectionHeading
            title="Alarms"
            description="Newest faults"
            action={<Button type="button" variant="outline" size="sm" className="rounded-full" onClick={() => navigate('/alarms')}>View all</Button>}
          />
          {recentAlarms.length === 0 ? (
            <EmptyState compact icon={AlertTriangle} title="No alarms" description="Faults from charge points will be listed here." />
          ) : (
            <div className="space-y-2">
              {recentAlarms.map((alarm) => (
                <button
                  key={alarm.id}
                  type="button"
                  className="flex w-full items-start justify-between gap-3 rounded-2xl bg-black/[0.03] px-3 py-3 text-left transition-colors hover:bg-black/[0.06] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring dark:bg-white/[0.04] dark:hover:bg-white/[0.08]"
                  onClick={() => navigate('/alarms')}
                  data-testid={`alarm-${alarm.id}`}
                >
                  <div className="min-w-0">
                    <p className="truncate font-medium">{alarm.error_code}</p>
                    <p className="line-clamp-2 text-xs text-muted-foreground">{alarm.message || alarm.charge_point_id}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{timeAgo(alarm.last_seen || alarm.first_seen)}</p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1">
                    <SeverityBadge severity={alarm.severity} />
                    <StatusBadge status={alarm.status} variant="dot" />
                  </div>
                </button>
              ))}
            </div>
          )}
        </GlassCard>
      </div>
    </Page>
  );
};

export default DashboardPage;
